import moment from 'moment';
import { defineRule, Field, Form, ErrorMessage} from 'vee-validate';
import { required, length, numeric, decimal, alpha_num, regex, max, min_value, email, min, one_of,} from '@vee-validate/rules';
import taiwanId from './taiwanId';
import dateTransform from './dateTransform';

// ======================= 自定義validation (回傳true/false) =============================== //
/** 地址檢核 */
const validateAddress = function(value){
    if(!value) return false;
    return ['號', '号', '信箱'].some(substring => value.includes(substring));
};

/** 民國生日格式
 *  使用方式舉例: rules="rocDate" 或 :rules="{rocDate: true}"
 * @param {String} value - 欄位值, 格式應為 YYMMDD 或 YYYMMDD
 * @param {String} type - birthday(需小於今日) / policy(需大於等於今日) 
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
const rocDate = function(value, type){
    if(!value || (!/^\d{6,7}$/.test(value) && !/^\d{2,3}\/[0-1]\d\/[0-3]\d$/.test(value))) return false; // 空值
    value = value.replace(/\//g, '');

    let date  = value.slice(-2);
    let month = value.slice(-4, -2);
    let year = value.slice(0, -4);

    // 檢核有效日期
    if(!moment(`${ parseInt(year) + 1911}-${month}-${date}`, 'YYYY-MM-DD').isValid()) return false; //日期確認

    let thisYear = moment().year();

    if (type == 'policy') {
        if(year < (thisYear-1911) || year < 0) return false; // 要保日年份需大於等於今年
    } else {
        if(year >= (thisYear-1911) || year < 0) return false; // 生日年份需小於今年
    }

    return true;
};

/** 判定西元日期格式
 *  如果 compareDate 沒有值就忽略進行比較，只判定value
 *  如果 有operator但沒有compareDate 則判定為錯誤
 *  @param {String} value - 西元日期格式 YYYYMMDD
 *  @param {String} operator - 比較方式 會讀取 ">, >=, <, <=, ==, !=", 其他值忽略
 *  @param {String} compareDate - 比較西元日期 YYYY-MM-DD。
*/
const verifyDate = function(value, { operator = '', compareDate = null}){
    if (!value || (!/^\d{8}$/.test(value) && !/^\d{4}\/[0-1]\d\/[0-3]\d$/.test(value))) return false;
    value = value.replace(/\//g, '');

    let day  = value.slice(-2);
    let month = value.slice(-4, -2);
    let year = value.slice(0, -4);

    if (year < 1911) return false;

    // 檢核有效日期
    let baseValidate = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD').isValid();
    let compareValidate = true;

    if(baseValidate && !!compareDate && operator != ''){
        baseValidate = baseValidate && moment(compareDate, 'YYYY-MM-DD').isValid();

        if(baseValidate){
            let date = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD');
            let toCompareDate = moment(compareDate, 'YYYY-MM-DD');
            switch(operator){
                case '>':
                    compareValidate = date > toCompareDate;
                    break;
                case '>=':
                    compareValidate = date >= toCompareDate;
                    break;
                case '<':
                    compareValidate = date < toCompareDate;
                    break;
                case '<=':
                    compareValidate = date <= toCompareDate;
                    break;
                case '==':
                    compareValidate = date == toCompareDate;
                    break;
                case '!=':
                    compareValidate = date != toCompareDate;
                    break;
                default: 
            }
        }
    }

    return baseValidate && compareValidate;
};

/** 起保日期是否晚於特定日期
 *  使用方式舉例: rules="afterDateWithRoc:2023-02-02,2023-01-01" 
 *           或 :rules="{ afterDateWithRoc: [minDate='2023-02-02', text='2023-01-01'] }"
 * @param {String} value - 欄位值, 民國日期，格式應為 YY/MM/DD 或 YYY/MM/DD
 * @param {String} minDate - 西元日期 YYYY-MM-DD
 * @param {String} text - 對比資料文字
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
const afterDateWithRoc = function(value, minDate=null){
     if (!value || !value.match(/^\d{2,3}\/(0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[0-1])$/)) return false;
    let rocDate = dateTransform.rocToCe(value);

    if(!moment(rocDate.join('-')).isValid()) return false;

    return (moment(rocDate.join('-')) >= moment(minDate));
};



// ======================= 以下為 vee-validate 自訂Global規則及自訂錯誤訊息 =============================== //
// 規則參考：https://vee-validate.logaretm.com/v4/guide/global-validators#vee-validaterules

/** 必須為 non-empty value。
 *  empty value 包含 "", undefined, null, false, []
 *  使用方式舉例:  rules="required" 或 :rules="{ required: true }"
 * @param {Any} value - 欄位值
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('required', 
    (value, args, ctx) => {
        return required(value) ? true : `請填寫${ctx.label || ctx.name || ''}`;
    }
);

/** 必須為數字
 *  使用方式舉例: rules="numeric" 或 :rules="{ numeric: true }"
 * @param {Any} value - 欄位值
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('numeric', (value, args, ctx) => {
    return numeric(value) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 必須為數字可以是小數 for機車排氣量
 *  使用方式舉例: rules="numeric" 或 :rules="{ numeric: true }"
 * @param {Any} value - 欄位值
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
// 自定義 decimal 規則
defineRule('decimal', (value, { decimals = '*', separator = '.' } = {}, ctx) => {
    if (typeof value !== 'string' && typeof value !== 'number') {
        return `${ctx.label || ctx.name || ''}格式有誤`;
    }

    // 將數字轉換為字符串以進行正則表達式匹配
    const stringValue = String(value);
    const regex = decimals === '*' 
        ? new RegExp(`^-?\\d+(\\${separator}\\d+)?$`) 
        : new RegExp(`^-?\\d+(\\${separator}\\d{0,${decimals}})?$`);
    return regex.test(stringValue) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 其中之一
 *  使用方式舉例: rules="oneOf:FAMILY,OTHER" 或 :rules="{oneOf: ['FAMILY', 'OTHER']}"
 * @param {Any} value - 欄位值
 * @param {Array} args - 參數
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('oneOf', (value, args, ctx) => {
    return one_of(value, args) ? true : `${ctx.label || ctx.name || ''}不符合條件`;
});

/** item 必須限制為 x 長度
 *  使用方式舉例: rules="length:5" 或 :rules="{length: 5}"
 * @param {String|Array} value - 欄位值
 * @param {String|Number} args - 參數
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('length', (value, args, ctx) => {
    return length(value, args) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 字母或數字
 *  使用方式舉例: rules="alpha_num" 或 :rules="{alpha_num: true}"
 * @param {String|Array} value - 欄位值
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('alpha_num', (value, args, ctx) => {
    return alpha_num(value) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 自訂 regex
 *  使用方式舉例: rules="regex:^09\d{8}$" 或 :rules="{regex: /^09\d{8}$/}"
 * @param {String} value - 欄位值
 * @param {String|Regex} args - 參數
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('regex', (value, args, ctx) => {
    return regex(value, args) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 長度最大值
 *  使用方式舉例: rules="max:10" 或 :rules="{max: 10}"
 * @param {String|Array} value - 欄位值
 * @param {String|Number} args - 參數
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('max', (value, args, ctx) => {
    return max(value, args) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 長度最小值
 *  使用方式舉例: rules="min:3" 或 :rules="{min: 3}"
 * @param {String|Array} value - 欄位值
 * @param {String|Number} args - 參數
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('min', (value, args, ctx) => {
    return min(value, args) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 長度最小數值
 *  使用方式舉例: rules="min_value:3" 或 :rules="{min_value: 3}"
 * @param {String|Number} value - 欄位值
 * @param {String|Number} args - 參數
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('min_value', (value, args, ctx) => {
    return min_value(value, args) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 必須為email
 *  使用方式舉例: rules="email" 或 :rules="{email: true}"
 * @param {String|Number} value - 欄位值
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('email', (value, args, ctx) => {
    return email(value) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

// ======================= Customized Validation =============================== //
 
/** 地址格式
 *  使用方式舉例: rules="address" 或 :rules="{address: true}"
 * @param {String} value - 欄位值
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('address', (value, args, ctx) => {
    if(!validateAddress(value)){
        return `${ctx.label || ctx.name || ''}格式有誤`;
    }
    return true;
});

/** 身分證、居留證、公司統編
 *  使用方式舉例: rules="taiwanId" 或 :rules="{taiwanId: true}"
 * @param {String} value - 欄位值
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('taiwanId', (value, args, ctx) => {
    return taiwanId.validateAll(value) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 自然人ID
 *  使用方式舉例: rules="taiwanPersonalId" 或 :rules="{taiwanPersonalId: true}"
 * @param {String} value - 欄位值
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('taiwanPersonalId', (value, args, ctx) => {
    return taiwanId.validatePersonal(value) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 民國生日格式
 *  使用方式舉例: rules="rocDate" 或 :rules="{rocDate: true}"
 * @param {String} value - 欄位值, 格式應為 YYMMDD 或 YYYMMDD
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('rocDate', (value, args, ctx) => {
    if(!value) return true;
    return rocDate(value) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 原發照日期是否早於本月
 *  使用方式舉例: rules="issueBefore" 或 :rules="{issueBefore: true}"
 * @param {String} value - 欄位值, 民國年月，格式應為 YY/MM 或 YYY/MM
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('issueBefore', (value, args, ctx) => {

    // if (!value || !value.match(/^\d{2,3}\/(0[1-9]|1[0-2])$/)){
    //     return `${ctx.label || ctx.name || ''}格式有誤`;
    // } 

    let rocDate = dateTransform.rocToCe(value + '/01');
    return (moment(rocDate.join('-')) <= moment()) ? true : `${ctx.label || ctx.name || ''}需早於今日`;
});

/** 原發照日期是否晚於特定年月份(value 為 user 輸入之民國日期)
 *  使用方式舉例: rules="issueAfter:2022-01-01,出廠年月" 
 *           或 :rules="{ issueAfter:[minDate='2022-01-01', text='出廠年月'] }"
 * @param {String} value - 欄位值, 民國年月，格式應為 YY/MM 或 YYY/MM
 * @param {String} minDate - 西元日期 YYYY-MM-DD
 * @param {String} text - 對比資料文字
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('issueAfter', (value, [minDate, text], ctx) => {
    let errMsg = `${ctx.label || ctx.name || ''}最早需從${text}開始`;

    // if (!value || !value.match(/^\d{2,3}\/(0[1-9]|1[0-2])$/)) return errMsg; //格式
    if (!value) return errMsg; //格式

    let rocDate = dateTransform.rocToCe(value + '/01');
    return (moment(rocDate.join('-')) >= moment(minDate)) ? true : errMsg;
});

/** 起保日期是否晚於特定日期
 *  使用方式舉例: rules="afterDateWithRoc:2023-02-02,2023-01-01" 
 *           或 :rules="{ afterDateWithRoc: [minDate='2023-02-02', text='2023-01-01'] }"
 * @param {String} value - 欄位值, 民國日期，格式應為 YY/MM/DD 或 YYY/MM/DD
 * @param {String} minDate - 西元日期 YYYY-MM-DD
 * @param {String} text - 對比資料文字
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('afterDateWithRoc', (value, [minDate=null, text=''], ctx) => {
     let errMsg = `${ctx.label || ctx.name || ''}需晚於${text || ''}`;
    return afterDateWithRoc(value, minDate) ? true : errMsg;
});

/** 判定西元日期格式
 *  如果 compareDate 沒有值就忽略進行比較，只判定value
 *  如果 有operator但沒有compareDate 則判定為錯誤
 *  使用方式舉例: rules="verifyDate" 或 :rules="{ verifyDate: true }"
 *           或 :rules="{afterDateWithRoc: [ minDate: '2023-02-02', text: '2023-01-01'}]"
 * @param {String} value - 西元日期格式 YYYYMMDD
 * @param {String} operator - 比較方式 會讀取 ">, >=, <, <=, ==, !=", 其他值忽略
 * @param {String} compareDate - 比較西元日期 YYYY-MM-DD。
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('verifyDate', (value, [operator = '', compareDate=null], ctx) => {
    if(!value) return true;
    return (verifyDate(value, {operator:operator, compareDate:compareDate})) ? true : `${ctx.label || ctx.name || ''}格式有誤`;
});

/** 檢核物件的value不可為null
 *  使用方式舉例: rules="objValue" 或 :rules="{ objValue: true }"
 * @param {Object} obj - 物件
 * @param {Any} args - 參數 (不需要)
 * @param {Object} ctx - 欄位物件
 * @returns {Boolean|String} - true 或 errorMessage
 */
defineRule('objValue', (obj, args, ctx) => {
    return (typeof obj === 'object' && !!obj.value) ? true : `請填寫${ctx.label || ctx.name || ''}`;
});

export {
    Form,
    Field,
    ErrorMessage,
    defineRule,
    validateAddress,
    verifyDate,
    rocDate,
    afterDateWithRoc,
    required
};
